import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { surveyPublicApi } from '../../services/surveyApi';
import { Survey } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';
import * as XLSX from 'xlsx';

const SurveyDetail: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (surveyId) loadSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSurvey(surveyId!);
      setSurvey(data);
    } catch (err) {
      console.error('Failed to load survey:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(t('surveyDetail', 'sendConfirm'))) return;
    try {
      const updated = await apiClient.sendSurvey(surveyId!);
      setSurvey(updated);
      alert(t('surveyDetail', 'sendSuccess'));
    } catch (err: any) {
      alert(t('surveyDetail', 'sendFailed') + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleClose = async () => {
    if (!window.confirm(t('surveyDetail', 'closeConfirm'))) return;
    try {
      const updated = await apiClient.closeSurvey(surveyId!);
      setSurvey(updated);
    } catch (err: any) {
      alert(t('surveyDetail', 'closeFailed'));
    }
  };

  const handleToggleAi = async () => {
    try {
      const updated = await apiClient.updateSurvey(surveyId!, { aiEnabled: !survey?.aiEnabled });
      setSurvey(updated);
    } catch (err: any) {
      alert('AI設定の更新に失敗しました');
    }
  };

  const handleDownloadExcel = async (respondentToken: string, respondentName: string) => {
    try {
      // Fetch the response data using public survey API
      const surveyData = await surveyPublicApi.getSurveyByToken(respondentToken);
      if (!surveyData.existingResponse) {
        alert('回答データが見つかりません');
        return;
      }

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Prepare data for Excel
      const data = surveyData.existingResponse.responses.map((a: any, idx: number) => ({
        '番号': idx + 1,
        'カテゴリ': a.category || '',
        '質問': a.question,
        '回答': a.answer || '',
        '備考': a.notes || ''
      }));

      const ws = XLSX.utils.json_to_sheet(data);

      // Set column widths
      ws['!cols'] = [
        { wch: 5 },   // 番号
        { wch: 20 },  // カテゴリ
        { wch: 50 },  // 質問
        { wch: 50 },  // 回答
        { wch: 30 }   // 備考
      ];

      XLSX.utils.book_append_sheet(wb, ws, '回答');

      // Download
      const fileName = `${survey?.title || 'survey'}_${respondentName}_${new Date().toLocaleDateString('ja-JP').replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Excel download failed:', err);
      alert('Excelダウンロードに失敗しました');
    }
  };

  const handleDownloadPdf = async (respondentToken: string, respondentName: string) => {
    try {
      // Fetch the response data using public survey API
      const surveyData = await surveyPublicApi.getSurveyByToken(respondentToken);
      if (!surveyData.existingResponse) {
        alert('回答データが見つかりません');
        return;
      }

      // Create a printable HTML document
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${survey?.title || 'アンケート'} - ${respondentName}</title>
          <style>
            body { font-family: 'Hiragino Kaku Gothic Pro', 'メイリオ', sans-serif; padding: 20px; }
            h1 { font-size: 1.5rem; margin-bottom: 8px; }
            .meta { color: #666; font-size: 0.9rem; margin-bottom: 20px; }
            .question { margin-bottom: 20px; padding: 16px; border: 1px solid #e0e0e0; border-radius: 8px; }
            .question-header { display: flex; gap: 8px; margin-bottom: 8px; }
            .question-num { color: #999; font-size: 0.85rem; }
            .question-category { background: #ebf8ff; color: #2c5282; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; }
            .question-text { font-weight: 600; margin-bottom: 12px; }
            .answer { background: #f7fafc; padding: 12px; border-radius: 4px; white-space: pre-wrap; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>${survey?.title || 'アンケート'}</h1>
          <div class="meta">回答者: ${respondentName} | 出力日: ${new Date().toLocaleDateString('ja-JP')}</div>
          ${surveyData.existingResponse.responses.map((a: any, idx: number) => `
            <div class="question">
              <div class="question-header">
                <span class="question-num">#${idx + 1}</span>
                ${a.category ? `<span class="question-category">${a.category}</span>` : ''}
              </div>
              <div class="question-text">${a.question}</div>
              <div class="answer">${a.answer || '（未回答）'}</div>
            </div>
          `).join('')}
        </body>
        </html>
      `;

      // Open print window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('PDFダウンロードに失敗しました');
    }
  };

  const handleEditResponse = (respondentToken: string) => {
    // Navigate to the survey response page with edit mode
    window.open(`${window.location.origin}/survey/${respondentToken}?edit=true`, '_blank');
  };

  const statusStyle = (status: string) => {
    switch (status) {
      case 'pending': return { text: t('surveyDetail', 'statusPending'), color: '#718096', bg: '#edf2f7' };
      case 'sent': return { text: t('surveyDetail', 'statusSent'), color: '#2b6cb0', bg: '#ebf8ff' };
      case 'in_progress': return { text: t('surveyDetail', 'statusInProgress'), color: '#d69e2e', bg: '#fffff0' };
      case 'completed': return { text: t('surveyDetail', 'statusCompleted'), color: '#276749', bg: '#f0fff4' };
      default: return { text: status, color: '#718096', bg: '#edf2f7' };
    }
  };

  if (loading) return <div style={{ padding: '30px' }}>{t('common', 'loading')}</div>;
  if (!survey) return <div style={{ padding: '30px' }}>{t('surveyDetail', 'notFound')}</div>;

  const respondents = survey.respondents || [];
  const completedCount = respondents.filter(r => r.status === 'completed').length;

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/admin/surveys')}
        style={{ marginBottom: '16px', padding: '6px 12px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#4a5568' }}
      >
        {t('surveyDetail', 'backToList')}
      </button>

      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: '0 0 8px' }}>{survey.title}</h2>
            <div style={{ fontSize: '0.9rem', color: '#718096' }}>
              <span>{t('survey', 'responses')} {completedCount}/{respondents.length}{t('survey', 'respondentCount')}</span>
              <span style={{ margin: '0 12px' }}>|</span>
              <span>{t('survey', 'deadline')} {new Date(survey.expiresAt).toLocaleDateString('ja-JP')}</span>
              <span style={{ margin: '0 12px' }}>|</span>
              <span>{t('common', 'status')}: {survey.status === 'draft' ? t('survey', 'statusDraft') : survey.status === 'active' ? t('survey', 'statusActive') : t('survey', 'statusClosed')}</span>
              <span style={{ margin: '0 12px' }}>|</span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={survey.aiEnabled !== false}
                  onChange={handleToggleAi}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                <span>AIアシスト</span>
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {survey.status === 'draft' && (
              <button onClick={handleSend} style={{ padding: '8px 16px', backgroundColor: '#2c5282', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {t('surveyDetail', 'sendEmail')}
              </button>
            )}
            {survey.status !== 'closed' && (
              <button onClick={handleClose} style={{ padding: '8px 16px', backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {t('surveyDetail', 'close')}
              </button>
            )}
            <button
              onClick={() => navigate(`/admin/surveys/${surveyId}/results`)}
              style={{ padding: '8px 16px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              {t('surveyDetail', 'viewResults')}
            </button>
          </div>
        </div>
      </div>

      {/* Respondent list */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0 0 16px' }}>{t('surveyDetail', 'respondentList')}</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f7fafc' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem' }}>{t('common', 'name')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem' }}>{t('common', 'email')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem' }}>{t('common', 'status')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem' }}>{t('surveyDetail', 'url')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontSize: '0.85rem' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {respondents.map((r, i) => {
              const s = statusStyle(r.status);
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 12px' }}>{r.name}</td>
                  <td style={{ padding: '10px 12px', color: '#718096', fontSize: '0.9rem' }}>{r.email}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <span style={{ padding: '3px 8px', backgroundColor: s.bg, color: s.color, borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600 }}>
                      {s.text}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/survey/${r.token}`);
                        alert(t('surveyDetail', 'urlCopied'));
                      }}
                      style={{ padding: '4px 10px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', color: '#4a5568' }}
                    >
                      {t('surveyDetail', 'copyUrl')}
                    </button>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      {(r.status === 'completed' || r.status === 'in_progress') && (
                        <>
                          <button
                            onClick={() => handleEditResponse(r.token)}
                            style={{ padding: '4px 8px', backgroundColor: '#4299e1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                          >
                            編集
                          </button>
                          <button
                            onClick={() => handleDownloadExcel(r.token, r.name)}
                            style={{ padding: '4px 8px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                          >
                            Excel
                          </button>
                          <button
                            onClick={() => handleDownloadPdf(r.token, r.name)}
                            style={{ padding: '4px 8px', backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                          >
                            PDF
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SurveyDetail;
